import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import PersonnelService from '../../services/PersonnelService';
import RoleService from '../../services/RoleService';
import UtilisateurService from '../../services/UtilisateurService';

const OffcanvasAddUtilisateurForm = ({ onClick }) => {
	const [personnelOptions, setPersonnelOptions] = useState([]);
	const [roleOptions, setRoleOptions] = useState([]);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');

	useEffect(() => {
		const fetchOptions = async () => {
			try {
				const [personnels, roles] = await Promise.all([
					PersonnelService.getPersonnelOptions(),
					RoleService.getAllRoles()
				]);
				setPersonnelOptions(personnels);
				setRoleOptions(
					roles.map((role) => ({
						value: role.id,
						label: role.nom.toUpperCase()
					}))
				);
			} catch (error) {
				console.error('Error fetching options', error);
			}
		};

		fetchOptions();
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const utilisateurData = {
					personnel: form.personnel.value,
					username: form.username.value,
					password: form.password.value,
					role: form.role.value
				};

				const response = await UtilisateurService.createUtilisateur(
					utilisateurData
				);

				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				window.location.href = '/administration/utilisateurs';
			} catch (error) {
				console.error("Erreur lors de la création de l'utilisateur", error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				{/* Personnel */}
				<Col xs={12} className="mb-3">
					<Form.Group>
						<Form.Label htmlFor="personnel">
							Personnel <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="personnel"
							required
							isInvalid={!!feedbackMessages['personnel']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									personnel: e.target.value
										? ''
										: "L'employé associé à cet utilisateur est requis."
								}));
							}}
						>
							<option value="">Sélectionner un employé</option>
							{personnelOptions.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['personnel']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Nom d'utilisateur */}
				<Col xs={12} className="mb-3">
					<Form.Group>
						<Form.Label htmlFor="username">
							Nom d'utilisateur <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="username"
							type="email"
							placeholder="nom@example.com"
							id="username"
							required
							isInvalid={!!feedbackMessages['username']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									username: e.target.value
										? ''
										: "Le nom d'utilisateur est requis."
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['username']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Mot de passe */}
				<Col xs={12} className="mb-3">
					<Form.Group>
						<Form.Label htmlFor="password">
							Mot de passe <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="password"
							type="password"
							placeholder="Saisir le mot de passe"
							id="password"
							required
							isInvalid={!!feedbackMessages['password']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									password: e.target.value ? '' : 'Le mot de passe est requis.'
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['password']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Confirmation du mot de passe */}
				<Col xs={12} className="mb-3">
					<Form.Group>
						<Form.Label htmlFor="confirmPassword">
							Confirmer le mot de passe <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="confirmPassword"
							type="password"
							placeholder="Confirmer le mot de passe"
							id="confirmPassword"
							required
							isInvalid={!!feedbackMessages['confirmPassword']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									confirmPassword: e.target.value
										? ''
										: 'La confirmation du mot de passe est requise.'
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['confirmPassword']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Role */}
				<Col xs={12} className="mb-3">
					<Form.Group>
						<Form.Label htmlFor="role">
							Rôle <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="role"
							required
							isInvalid={!!feedbackMessages['role']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									role: e.target.value ? '' : 'Le rôle est requis.'
								}));
							}}
						>
							<option value="">Sélectionner un rôle</option>
							{roleOptions.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['role']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddUtilisateurForm;
